import { useEffect, useState, useSyncExternalStore } from 'react';
import { estEffetDeFiche, referenceMcp, typeDeFichierAccepte } from '@taki/data-model';
import type { AppScreenAction, FichiersDeJeu } from '@taki/data-model';
import type { MarqueDeDocument } from '@taki/pdf';
import type { TakiSdk } from '@taki/sdk';
import { useBrandingPort } from '@taki/ui';
import type { ScreenAction, ViewBinding } from '@taki/ui';
import { cleDExport, exporterEnPdf, ficheEnHtml, RAISON_PDF, suiviDesExports, type EtatDExport } from './fiche-en-pdf.js';

/**
 * LES GESTES DE FICHE : les actions declarees qui nomment un EFFET DE FICHE
 * (`EFFETS_DE_FICHE`, `@taki/data-model`), rendues dans le menu « ... » de la
 * fiche ouverte. La rangee 3a ne se rend qu'au niveau collection ; un geste qui
 * porte sur UNE fiche n'a de sens que la fiche ouverte.
 *
 * Le seul aujourd'hui, `socle:exporterPdf` : la fiche, telle que sa vue
 * `core.fiche` la montre (cartes de champs, libelles, valeurs de catalogue),
 * rendue en HTML a la marque de l'org (`@taki/pdf`), convertie par le serveur
 * (`sdk.files.genererPdf`) et rangee dans ses pieces jointes. Contrat :
 * `docs/PDF.md`.
 *
 * L'AVANCEMENT SE LIT DANS LA CARTE DES FICHIERS, pas dans le menu : le menu se
 * referme au clic, et c'est dans les pieces jointes que le PDF va apparaitre.
 * Le suivi est donc un petit magasin partage (`suiviDesExports`,
 * `fiche-en-pdf.ts`) que la carte (`FilesPanel`) et le menu lisent tous deux.
 */

/** L'etat de l'export de cette fiche, relu a chaque changement. */
export function useEtatDExport(cle: string | null): EtatDExport | undefined {
  return useSyncExternalStore(
    suiviDesExports.abonner,
    () => (cle === null ? undefined : suiviDesExports.lire(cle)),
  );
}

/** L'id de l'entree « Copier l'ID MCP » du menu « ... » de toute fiche. */
export const GESTE_COPIER_ID_MCP = 'socle:copier-id-mcp';
/** Combien de temps l'entree dit « Copié », en ms : le temps de le lire, comme le bouton « Copier » des reglages. */
export const DUREE_DE_LA_COPIE = 2000;
export const RAISON_COPIE_IMPOSSIBLE = 'Le navigateur ne permet pas de copier ici.';

/** Le presse-papiers, tel que le navigateur le prete ; `null` sans lui. */
function pressePapiers(): { writeText(texte: string): Promise<void> } | null {
  if (typeof navigator === 'undefined') return null;
  const clipboard = (navigator as { clipboard?: { writeText?: (texte: string) => Promise<void> } }).clipboard;
  return typeof clipboard?.writeText === 'function' ? (clipboard as { writeText(texte: string): Promise<void> }) : null;
}

/**
 * « COPIER L'ID MCP » (chantier `fiche-suivi`, `docs/socle/fiche-suivi.md`,
 * section 7) : la reference `mytaki://<hote>/<famille>/<id>` de la fiche, que
 * `read_record` et Lia reconnaissent. L'hote est celui de la page : c'est
 * l'environnement ou la fiche vit. `null` quand la reference ne se forme pas
 * (un hote ou un id hors de sa grammaire) : l'entree le dit eteinte.
 */
export function referenceDeLaFiche(hote: string, famille: string, id: string): string | null {
  try {
    return referenceMcp({ hote, famille, id });
  } catch {
    return null;
  }
}

export interface OptionsDesGestesDeFiche<T> {
  sdk: TakiSdk;
  /** Les actions DECLAREES de la porte active ; seules celles qui nomment un effet de fiche comptent. */
  actions: ReadonlyArray<Pick<AppScreenAction, 'id' | 'label' | 'fn'>> | undefined;
  /** Le nom de la porte active. */
  porte: string;
  jeu: { family: string; type?: string | undefined } | null;
  politique: FichiersDeJeu | null;
  /** L'id de la fiche ouverte, `null` quand ce n'est pas une fiche en base. */
  ficheOuverte: string | null;
  record: T | undefined;
  binding: ViewBinding<T> | undefined;
  /** La raison pour laquelle la porte ne s'ecrit pas (`useScreen().raisonAcces`). */
  raisonAcces: string | null;
}

/**
 * Les entrees du menu « ... » de la fiche ouverte que la declaration demande.
 * A AJOUTER a celles du moteur : `recordMenuActions={[...screen.recordMenuActions, ...gestes]}`.
 * Une entree qui ne peut pas servir reste VISIBLE, eteinte, avec sa raison (G16).
 */
export function useGestesDeFiche<T>(options: OptionsDesGestesDeFiche<T>): ScreenAction[] {
  const marqueCourante = useBrandingPort()?.courant;
  const { sdk, jeu, ficheOuverte, record, binding, politique } = options;
  const cle = jeu !== null && ficheOuverte !== null ? cleDExport(jeu.family, ficheOuverte) : null;
  const suivi = useEtatDExport(cle);
  /* L'ACCUSE DE LA COPIE : l'entree dit « Copié » le temps de le lire, pour la
     fiche copiee seulement. */
  const [copiee, setCopiee] = useState<string | null>(null);
  useEffect(() => {
    if (copiee === null) return;
    const minuteur = setTimeout(() => setCopiee(null), DUREE_DE_LA_COPIE);
    return () => clearTimeout(minuteur);
  }, [copiee]);
  if (jeu === null || ficheOuverte === null) return [];
  const hote = typeof location === 'undefined' ? '' : location.host;
  const reference = referenceDeLaFiche(hote, jeu.family, ficheOuverte);
  const papiers = pressePapiers();
  const raisonDeCopie = papiers === null ? RAISON_COPIE_IMPOSSIBLE
    : reference === null ? "Cette fiche n'a pas de référence MCP." : null;
  const copier: ScreenAction = {
    id: GESTE_COPIER_ID_MCP,
    label: copiee === ficheOuverte ? 'Copié' : 'Copier l’ID MCP',
    variant: 'secondary',
    /* L'ACCUSE EST L'ENTREE ELLE-MEME : le menu reste ouvert le temps de le lire. */
    garderLeMenuOuvert: DUREE_DE_LA_COPIE,
    disabled: raisonDeCopie !== null,
    ...(raisonDeCopie === null ? {} : { disabledReason: raisonDeCopie }),
    onClick: () => {
      if (papiers === null || reference === null) return;
      const copieDe = ficheOuverte;
      void papiers.writeText(reference).then(() => setCopiee(copieDe), () => undefined);
    },
  };
  if (record === undefined || binding === undefined) return [copier];
  const declares = (options.actions ?? [])
    .filter((action) => estEffetDeFiche(action.fn))
    .map((action): ScreenAction => {
      const raison = options.raisonAcces
        ?? (politique === null ? RAISON_PDF.fichiersFermes
          : !typeDeFichierAccepte(politique, 'application/pdf') ? RAISON_PDF.pdfRefuse
          : suivi?.etat === 'enCours' ? RAISON_PDF.enCours
          : null);
      return {
        id: action.id,
        label: suivi?.etat === 'enCours' ? 'PDF en cours...' : action.label,
        variant: 'secondary',
        disabled: raison !== null,
        ...(raison === null ? {} : { disabledReason: raison }),
        onClick: () => {
          const marque: MarqueDeDocument = {
            nom: marqueCourante?.name ?? 'MyTaki',
            logo: marqueCourante?.logo ?? null,
            accent: marqueCourante?.accent ?? null,
          };
          const document = ficheEnHtml({ marque, porte: options.porte, binding, record, id: ficheOuverte });
          void exporterEnPdf(sdk, { collection: jeu.family, id: ficheOuverte }, document);
        },
      };
    });
  return [...declares, copier];
}
